import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { ChatMessage } from '@app/interfaces';
import { ChatRole } from '@app/enums';
import { ConversationMessage, ConversationState } from '../realtime-session-stream/conversation-message.interface';

/**
 * Service responsible for managing conversation state.
 * This is a pure state management service - no side effects, no API calls.
 */
@Injectable({
    providedIn: 'root'
})
export class ConversationStateService {
    // Primary conversation state
    private readonly stateSubject$ = new BehaviorSubject<ConversationState>({
        messages: [],
        isComplete: true,
        currentStreamingText: ''
    });

    // Public observables
    public readonly state$ = this.stateSubject$.asObservable();

    public readonly messages$ = this.state$.pipe(
        map((state) => state.messages),
        distinctUntilChanged()
    );

    public readonly isComplete$ = this.state$.pipe(
        map((state) => state.isComplete),
        distinctUntilChanged()
    );

    public readonly isStreaming$ = this.state$.pipe(
        map((state) => !state.isComplete),
        distinctUntilChanged()
    );

    public readonly streamingMessageIndex$ = this.state$.pipe(
        map((state) => state.streamingMessageIndex),
        distinctUntilChanged()
    );

    // Filtered conversation for API (excludes feedback prompts)
    public readonly messagesForAgent$ = this.messages$.pipe(
        map((messages) => this.filterMessagesForAgent(messages)),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    );

    // Current state getters
    get currentState(): ConversationState {
        return this.stateSubject$.value;
    }

    get messages(): ConversationMessage[] {
        return this.currentState.messages;
    }

    get streamingMessageIndex(): number | undefined {
        return this.currentState.streamingMessageIndex;
    }

    /**
     * Update the entire conversation state
     */
    setState(state: ConversationState): void {
        this.stateSubject$.next(state);
    }

    /**
     * Update partial state (merges with current)
     */
    updateState(updates: Partial<ConversationState>): void {
        const currentState = this.currentState;
        this.setState({
            ...currentState,
            ...updates
        });
    }

    /**
     * Set all messages at once
     */
    setMessages(messages: ConversationMessage[]): void {
        this.updateState({ messages });
    }

    /**
     * Add a single message
     */
    addMessage(message: ConversationMessage): void {
        // console.log(
        //     '➕ ConversationState.addMessage called. Current count:',
        //     this.messages.length,
        //     'Adding message type:',
        //     message.messageType
        // );
        const messages = [...this.messages, message];
        this.setMessages(messages);
        // console.log('✅ Message added. New count:', this.messages.length);
    }

    /**
     * Update a message by index
     */
    updateMessageByIndex(index: number, updates: Partial<ConversationMessage>): void {
        if (index < 0 || index >= this.messages.length) {
            console.error('Invalid message index:', index);
            return;
        }

        const messages = [...this.messages];
        messages[index] = {
            ...messages[index],
            ...updates
        };
        this.setMessages(messages);
    }

    /**
     * Update a message by messageId
     */
    updateMessageById(messageId: string, updates: Partial<ConversationMessage>): void {
        const index = this.messages.findIndex((m) => m.messageId === messageId);
        if (index !== -1) {
            this.updateMessageByIndex(index, updates);
        }
    }

    /**
     * Remove messages by type
     */
    removeMessagesByType(messageType: string): void {
        const messages = this.messages.filter((m) => m.messageType !== messageType);
        this.setMessages(messages);
    }

    /**
     * Set streaming state
     */
    setStreamingState(isStreaming: boolean, messageIndex?: number): void {
        // console.log('🎛️ setStreamingState called:', {
        //     isStreaming,
        //     messageIndex,
        //     currentIndex: this.streamingMessageIndex
        // });

        // CRITICAL FIX: Set the individual message's streaming state
        if (messageIndex !== undefined && messageIndex < this.currentState.messages.length) {
            const message = this.currentState.messages[messageIndex];
            if (message) {
                this.updateMessageByIndex(messageIndex, {
                    streamingState: {
                        ...message.streamingState,
                        isStreaming: isStreaming,
                        isComplete: !isStreaming
                    }
                });
            }
        }

        this.updateState({
            isComplete: !isStreaming,
            streamingMessageIndex: messageIndex,
            ...(isStreaming && { startTime: Date.now() })
        });
        // console.log('🎯 setStreamingState complete. New streaming index:', this.streamingMessageIndex);
    }

    /**
     * Mark streaming as complete
     */
    completeStreaming(): void {
        const currentState = this.currentState;
        this.updateState({
            isComplete: true,
            streamingMessageIndex: undefined,
            currentStreamingText: '',
            totalProcessingTime: currentState.startTime ? Date.now() - currentState.startTime : undefined
        });
    }

    /**
     * Reset conversation to initial state
     */
    reset(): void {
        this.setState({
            messages: [],
            isComplete: true,
            currentStreamingText: '',
            streamingMessageIndex: undefined
        });
    }

    /**
     * Filter messages for agent API
     */
    private filterMessagesForAgent(messages: ConversationMessage[]): ChatMessage[] {
        return messages.filter((msg) => {
            // Exclude feedback prompts
            if (msg.messageType === 'feedback_prompt') {
                return false;
            }

            // Include user messages
            if (msg.role === ChatRole.User) {
                return true;
            }

            // Include assistant messages (but not feedback thank you)
            if (msg.role === ChatRole.Assistant) {
                if (msg.requestId?.startsWith('feedback_thanks_')) {
                    return false;
                }
                return true;
            }

            return false;
        });
    }
}
